import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMG } from "@/lib/images";
import { Chapter, Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import ServiceCard from "@/components/ServiceCard";
import SectionCTA from "@/components/SectionCTA";
import { SERVICES, usePageMeta } from "@/lib/site";

export default function Services() {
  usePageMeta(
    "Construction Services in Utah",
    "Home additions, garages and garage apartments, ADU-style spaces, renovations, commercial construction, concrete, and full project management from Black Sky Builders in Salt Lake City."
  );

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Services — Black Sky Builders"
        lines={["What We Build"]}
        copy="Residential and commercial construction across Utah — additions, garages, ADU-style spaces, renovations, concrete, and complete project solutions managed from early planning through completion."
        image={IMG.tradesperson}
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="services-grid">
        <Chapter
          index="01"
          eyebrow="Full Scope"
          title="Every service, one accountable team"
          copy="Each project — large or small — gets the same treatment: clear scope, honest budgeting, reliable coordination, and communication you don't have to chase."
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.num} s={s} i={i} />
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-coal">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
          <Chapter
            index="02"
            eyebrow="Two Sides of the Work"
            title="Residential & commercial"
          />
          <div className="grid gap-5 md:grid-cols-2">
            <Reveal className="h-full">
              <div className="group flex h-full flex-col border border-line bg-ink" data-testid="services-residential-card">
                <div className="relative h-64 overflow-hidden">
                  <img src={IMG.addition} alt="Residential construction" loading="lazy" className="h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-6 sm:p-8">
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">Residential Construction</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">
                    Additions, expansions, garages, garage apartments, ADU-style spaces, multi-family and family
                    unit properties, renovations, and concrete — built around how you live and what your property can become.
                  </p>
                  <Link to="/residential-construction" data-testid="services-residential-link" className="link-arrow mt-6">
                    Explore Residential <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="h-full">
              <div className="group flex h-full flex-col border border-line bg-ink" data-testid="services-commercial-card">
                <div className="relative h-64 overflow-hidden">
                  <img src={IMG.commercial} alt="Commercial construction" loading="lazy" className="h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-6 sm:p-8">
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">Commercial Construction</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">
                    Tenant improvements, build-outs, expansions, and site solutions — managed with the coordination
                    and accountability commercial projects demand.
                  </p>
                  <Link to="/commercial-construction" data-testid="services-commercial-link" className="link-arrow mt-6">
                    Explore Commercial <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <SectionCTA />
    </div>
  );
}
